import React from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'dpVisitorAnalyticsUid';

const getSessionUid = (): string => {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing && existing.length >= 16) return existing;
    const generated = `dpv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 18)}`;
    window.localStorage.setItem(STORAGE_KEY, generated);
    return generated;
  } catch (error) {
    return `dpv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 18)}`;
  }
};

const getUtmValue = (name: string): string => {
  try {
    return new URLSearchParams(window.location.search).get(name) || '';
  } catch (error) {
    return '';
  }
};

const VisitorTracker: React.FC = () => {
  const location = useLocation();
  const startedAtRef = React.useRef<number>(Date.now());
  const landingUrlRef = React.useRef<string>(`${window.location.pathname}${window.location.search}`);
  const lastClickRef = React.useRef<{ text: string; target: string }>({ text: '', target: '' });
  const lastRouteRef = React.useRef<string>('');
  const lastClickSignatureRef = React.useRef<string>('');
  const lastClickAtRef = React.useRef<number>(0);

  const sendEvent = React.useCallback((eventType?: string, includeClick = false) => {
    const route = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const payload = {
      session_uid: getSessionUid(),
      source: 'public',
      route,
      title: document.title || 'DentalPuente',
      event: eventType || (lastRouteRef.current !== route ? 'pageview' : 'heartbeat'),
      referrer: document.referrer || '',
      landing_url: landingUrlRef.current,
      utm_source: getUtmValue('utm_source'),
      utm_medium: getUtmValue('utm_medium'),
      utm_campaign: getUtmValue('utm_campaign'),
      click_text: includeClick ? lastClickRef.current.text : '',
      click_target: includeClick ? lastClickRef.current.target : '',
      duration_seconds: Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000)),
    };
    lastRouteRef.current = route;

    try {
      fetch('/admin/api/visitor_track.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        cache: 'no-store',
        keepalive: true,
      }).catch(() => undefined);
    } catch (error) {
      // Analytics must never block the public site UX.
    }
  }, []);

  React.useEffect(() => {
    const timer = window.setTimeout(() => sendEvent('pageview'), 700);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search, location.hash, sendEvent]);

  React.useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('a,button,[role="button"]') as HTMLElement | null : null;
      if (!target) return;
      const text = (target.innerText || target.getAttribute('aria-label') || target.getAttribute('title') || target.getAttribute('name') || target.tagName || '').trim().slice(0, 255);
      const targetRef = (target.getAttribute('href') || target.getAttribute('data-bs-target') || target.getAttribute('name') || target.id || '').slice(0, 500);
      if (!text && !targetRef) return;
      const route = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const signature = `${route}|${text}|${targetRef}`;
      const now = Date.now();
      if (signature === lastClickSignatureRef.current && now - lastClickAtRef.current < 4500) return;
      lastClickSignatureRef.current = signature;
      lastClickAtRef.current = now;
      lastClickRef.current = { text, target: targetRef };
      sendEvent('click', true);
    };

    const interval = window.setInterval(() => {
      if (!document.hidden) sendEvent('heartbeat');
    }, 20000);
    const unloadHandler = () => {
      try {
        const route = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const payload = {
          session_uid: getSessionUid(), source: 'public', route, title: document.title || 'DentalPuente', event: 'unload',
          referrer: document.referrer || '', landing_url: landingUrlRef.current,
          duration_seconds: Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000)),
        };
        navigator.sendBeacon('/admin/api/visitor_track.php', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      } catch (error) {}
    };

    const visibilityHandler = () => {
      if (!document.hidden) sendEvent('heartbeat');
    };

    document.addEventListener('click', clickHandler, true);
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('beforeunload', unloadHandler);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('click', clickHandler, true);
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('beforeunload', unloadHandler);
    };
  }, [sendEvent]);

  return null;
};

export default VisitorTracker;
